/**
 * Turns chat as people type it into text a speech engine reads naturally.
 * Indonesian first, Malay second; other languages only get the language-neutral cleanup.
 */

/** Abbreviations common in both Indonesian and Malay chat. */
const SHARED: Record<string, string> = {
  yg: 'yang',
  dgn: 'dengan',
  utk: 'untuk',
  org: 'orang',
  sy: 'saya',
  lg: 'lagi',
  tp: 'tapi',
  jgn: 'jangan',
  bg: 'bang',
  kak: 'kak',
  dr: 'dari',
  sm: 'sama',
  byk: 'banyak',
  krn: 'karena',
  skrg: 'sekarang',
  dpt: 'dapat',
  blm: 'belum',
  sdh: 'sudah',
  jd: 'jadi',
  mmg: 'memang',
  btw: 'by the way',
  otw: 'on the way',
  gws: 'get well soon',
  pls: 'please',
  plz: 'please',
  thx: 'thanks',
  tq: 'thank you',
  ty: 'thank you',
  idk: "I don't know",
  omg: 'oh my god',
};

const INDONESIAN: Record<string, string> = {
  ...SHARED,
  gk: 'gak',
  ngga: 'nggak',
  tdk: 'tidak',
  udh: 'udah',
  bgt: 'banget',
  jg: 'juga',
  aj: 'aja',
  klo: 'kalo',
  kl: 'kalo',
  knp: 'kenapa',
  gmn: 'gimana',
  gmna: 'gimana',
  bs: 'bisa',
  bsa: 'bisa',
  trs: 'terus',
  km: 'kamu',
  kmu: 'kamu',
  kk: 'kakak',
  sprt: 'seperti',
  spt: 'seperti',
  hrs: 'harus',
  msh: 'masih',
  emg: 'emang',
  lgi: 'lagi',
  gw: 'gue',
  sblm: 'sebelum',
  slm: 'salam',
  ass: 'assalamualaikum',
  mksh: 'makasih',
  makasi: 'makasih',
  krn: 'karena',
};

const MALAY: Record<string, string> = {
  ...SHARED,
  x: 'tak',
  tk: 'tak',
  tak: 'tak',
  dh: 'dah',
  kt: 'kat',
  nk: 'nak',
  mcm: 'macam',
  cmne: 'macam mana',
  camne: 'macam mana',
  mne: 'mana',
  bkn: 'bukan',
  lps: 'lepas',
  tgk: 'tengok',
  blh: 'boleh',
  bleh: 'boleh',
  sbb: 'sebab',
  gnti: 'ganti',
  sgt: 'sangat',
  krn: 'kerana',
  kn: 'kan',
  sj: 'saja',
  bgs: 'bagus',
  pstu: 'pastu',
  sape: 'siapa',
  spe: 'siapa',
  ape: 'apa',
  pe: 'apa',
  cik: 'cik',
  bro: 'bro',
  tq: 'terima kasih',
  tqvm: 'terima kasih banyak',
};

function dictFor(lang: string | null | undefined): Record<string, string> {
  const l = (lang ?? '').toLowerCase();
  if (l.startsWith('id')) return INDONESIAN;
  if (l.startsWith('ms')) return MALAY;
  return {};
}

const EMOJI_RE = /[\p{Extended_Pictographic}\p{Emoji_Modifier}\u{1F1E6}-\u{1F1FF}‍️⃣]/gu;
const EMOTE_CODE_RE = /\[[a-z0-9_]+\]/gi;
const URL_RE = /\bhttps?:\/\/\S+/gi;

/** "wkwkwk", "wkwkwkwk", "hahaha", "hehe", "xixixi", "kwkw" all become a single short laugh. */
const LAUGH_RE = /\b(?:w?k(?:wk)+w?|(?:wk){2,}|(?:ha){2,}h?|(?:he){2,}h?|(?:hi){3,}|(?:xi){2,}|a?(?:ha){1,}ha)\b/gi;

/** Letters repeated three or more times collapse to one: "gatalll" -> "gatal", "bangettt" -> "banget". */
const STRETCH_RE = /(\p{L})\1{2,}/gu;

/** Indonesian and Malay reduplication shorthand: "kata2" -> "kata kata". */
const REDUP_RE = /\b(\p{L}{2,})2\b/gu;

export function normalizeForSpeech(text: string, lang?: string | null): string {
  const dict = dictFor(lang);
  let t = text
    .replace(URL_RE, ' ')
    .replace(EMOTE_CODE_RE, ' ')
    .replace(EMOJI_RE, ' ')
    .replace(LAUGH_RE, 'haha')
    .replace(STRETCH_RE, '$1')
    .replace(REDUP_RE, '$1 $1')
    // Runs of punctuation read as noise ("!!!!", "????", "....").
    .replace(/([!?.,])\1+/g, '$1')
    .replace(/[~*_^#|<>{}\\]+/g, ' ');

  if (Object.keys(dict).length) {
    t = t.replace(/\p{L}+/gu, (word) => {
      const hit = dict[word.toLowerCase()];
      return hit ?? word;
    });
  }
  return t.replace(/\s+/g, ' ').trim();
}

/**
 * Makes a display name speakable: drops emoji and decorations, turns separators into spaces
 * and trailing digits away ("nizar_99" -> "nizar"). Falls back to the handle, then to "".
 */
export function speakableName(nickname: string, uniqueId = ''): string {
  const clean = (s: string) =>
    s
      .replace(EMOJI_RE, ' ')
      .replace(/[^\p{L}\p{N}\s._-]/gu, ' ')
      .replace(/[._-]+/g, ' ')
      .replace(/(\p{L})\1{2,}/gu, '$1')
      .replace(/\s*\d+\s*$/u, '')
      .replace(/\s+/g, ' ')
      .trim();
  const n = clean(nickname);
  if (/\p{L}/u.test(n)) return n;
  const u = clean(uniqueId);
  return /\p{L}/u.test(u) ? u : '';
}
